import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Container, Form, Button, Row, Col, Alert } from 'react-bootstrap';
import { Auth, Hub } from 'aws-amplify';
import LoaderButton from '../components/LoaderButton';
import PasswordField from '../components/PasswordField';
import { useAppContext } from '../libs/contextLib';
import { useFormFields } from '../libs/hooksLib';
import { UiError } from '../libs/errorLib';
import { userTypeFromString, userTypeAsString, userTypes } from '../libs/userLib';
import { isAbsoluteURL } from '../libs/urllib';
import ReCAPTCHA from "react-google-recaptcha";
import config from '../config';

/**
 * Login page.
 *
 * Page will be customised to targeted user type.
 */
function Login({
  ...props
}) {
  const { setError }                                        = useAppContext();
  const { user, setUser }                                   = useAppContext();
  const { redirectInfo }                                    = useAppContext();
  const [isLoading, setIsLoading]                           = useState(false);
  const [federatedInfo, setFederatedInfo]                   = useState(null);
  const [federatedFirms, setFederatedFirms]                 = useState([]);
  const [renderedUserType, setRenderedUserType]             = useState(0);
  const [ captchaLoaded, setCaptchaLoaded ]                 = useState(false);
  const [ captchaRef, setCaptchaRef ]                       = useState(null)
  const [ captchaValue, setCaptchaValue ]                   = useState(null);
  const [ noLoginDetailsVisible, setNoLoginDetailsVisible ] = useState(false);
  const { search }                                          = useLocation();
  const navigate                                            = useNavigate();
  const { userType }                                        = useParams({userType: "none"});
  const [ fields, handleFieldChange ]                       = useFormFields(
    {
      email: '',
      password: '',
    },
    // Pass a callback as well.
    (fieldId, fieldValue) => {
      // We may use the entered email to make the user experience nicer when
      // requesting a password reset etc. Set it here.
      if (fieldId === 'email') {
        setUser({username: fieldValue});
        // If we have any federated firms, test the entered email address to
        // check whether we may want to use federated sign in.
        for (const firm of federatedFirms) {
          if (firm.regex.test(fieldValue)) {
            setFederatedInfo(
              {
                name: firm.name,
                provider: firm.provider,
              }
            )
            return;
          }
        }
        // We didn't match.
        setFederatedInfo(null);
      }
    }
  );

  /**
   * Compile the regex we use to search for the emails of federated firms on
   * first render so we don't do it for every typed character.
   */
  useEffect(
    () => {
      setFederatedFirms(
        config.federatedFirms.map(
          (firm) => {
            return(
              {
                name: firm.name,
                regex: new RegExp(firm.regex, 'i'),
                provider: firm.provider,
              }
            )
          }
        )
      )
    },
    []
  )

  /**
   * Opens outlink in new tab and tracks in Matomo.
   *
   * @param {*} event
   *    OnClick event. Will be preventDefault'ed.
   * @param {*} link
   *    Link to process outlink for.
   *
   * @returns 
   *    false.
   */
  //function outlink(event, link) {
  //  event.stopPropagation();
  //  event.nativeEvent.stopImmediatePropagation();
  //  event.preventDefault();
  //  trackLink({href: link, linkType: 'link'});
  //  window.open(link, '_blank').focus();
  //  return false;
  //}

  function validateForm() {
    return (
      fields.email.length > 0 && fields.password.length > 0 &&
      (
        captchaValue !== null ||
        !config.captcha.enabled
      )
    );
  }

  async function handleSubmit(event) {
    event.preventDefault();

    setIsLoading(true);
    // We'll set the email so we can potentially use it in Confirm.
    setUser({username: fields.email});
    // Auth handler will handle results of this call.
    try {
      let user = await Auth.signIn(
        {
          username: fields.email.trim(),
          password: fields.password.trim(),
          validationData: {
            captchaValue: captchaValue
          },
        }
      );
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        // This doesn't fire an auth event, so we'll do it mnaually.
        Hub.dispatch(
          'custom', 
          { 
            event: 'new_password_required', 
            data: { user: user}, 
            message:'' 
        });
        // Return so we don't do anything as this will redirect from authmanager
        // and unmount our component.
        if (captchaRef !== null) {
          captchaRef.reset();
        }
        setIsLoading(false);
        return;
      }
      else {
        new UiError('auth', '', 'resolved')
      }
    }
    catch (e) {
      let message = e.message;
      if (
        message === 'Temporary password has expired and must be reset by an administrator.'
      ) {
        message = 'Temporary password has expired and must be reset. Please click "I don\'t have login details".';
      }
      setError(
        new UiError('auth', "Error signing in: " + message, 'danger')
      );
      console.error(e)
      if (captchaRef !== null) {
        captchaRef.reset();
      }
      setIsLoading(false);
    }
  }

  // Pre-fill the username field if we already know it.
  useEffect(
    () => {
      if(user !== null && user.hasOwnProperty('username') && fields.email === '') {
        fields.email = user.username;
      }
    },
    [user, fields]
  );

  // Set the usertype we'll render.
  useEffect(
    () => {
      // If we're on the main login (before usertype is set) and the settings
      // haven't enabled the /login placeholder. Redirect the user.
      if (userType === "none" && config.links.loginUserType !== "/login") {
        let stateParam = '';
        if (typeof redirectInfo.state === 'string' && redirectInfo.state !== '') {
          stateParam = `&state=${redirectInfo.state}`
        }
        // We may be able to figure out what type of user we're dealing with
        // from the redirect_uri.
        if (
          typeof redirectInfo.redirect === 'string' &&
          redirectInfo.redirect.includes('AdviserPortal')
        ) {
          navigate(`/login/adviser?redirect_uri=${redirectInfo.redirect}${stateParam}`);
        }
        else if(
          typeof redirectInfo.redirect === 'string' &&
          redirectInfo.redirect.includes('CustomerPortal')
        ) {
          navigate(`/login/member?redirect_uri=${redirectInfo.redirect}${stateParam}`);
        }
        else {
          // Redirect only if we couldn't determine user type.
          // Check whether link is absolute, and redirect in the appropriate
          // manner.
          if(isAbsoluteURL(config.links.loginUserType)) {
            window.location = config.links.loginUserType;
          }
          else {
            navigate(config.links.loginUserType);
          }
        }
      }
      
      // Parse the string to a usertype.
      setRenderedUserType(userTypeFromString(userType, 0))
    },
    [userType, navigate, redirectInfo]
  );

  //############################################################################
  // These functions return different parts of the page depending on user type.
  // Return the controls when a bitmask returns true for a match.
  // Usertype can technically be 'all' (0b11111111) or 'none' (0b00000000) so
  // take this into account.
  //############################################################################

  /**
   * Return a username and password field for non-staff users.
   */
  function getUsernamePassword() {
    if(
      renderedUserType & (
        userTypes.member
        | userTypes.ifa
        | userTypes.paraplanner
        | userTypes.administrator
      )
    ) {
      return (
        <>
          <Form.Label className="form-label visually-hidden" htmlFor="email">Email</Form.Label>
          <Form.Control 
            className={federatedInfo === null ? "topinput" : ""}
            autoFocus
            id="email"
            placeholder="Email address"
            type="email"
            autoComplete="username"
            value={fields.email}
            onChange={handleFieldChange}
          />
          {
            // Don't show a password field when we have a federated user.
            federatedInfo === null ? 
            (
              <PasswordField 
                value={fields.password}
                onChange={handleFieldChange}
                className="bottominput"
                id="password"
                autoComplete="current-password"
              />
            ) : null
          }
        </>
      );
    }
    return(null);
  }

  /**
   * Return a login button for non-staff users.
   */
  function getLoginButton() {
    if(
      renderedUserType & (
        userTypes.member
        | userTypes.ifa
        | userTypes.paraplanner
        | userTypes.administrator
      )
    ) {
      if(federatedInfo === null) {
        return (
          <LoaderButton
            size="large"
            disabled={!validateForm()}
            type="submit"
            isLoading={isLoading}
          >
            Login
          </LoaderButton>
        );
      }
      else {
        return (
          <Button
            size="large"
            onClick={() => Auth.federatedSignIn({
              provider: federatedInfo.provider,
              customState: redirectInfo.getAsEncodedString()
            })}
          >
            Login with your {federatedInfo.name} account
          </Button>
        );
      }
    }
    return(null);
  }

  /**
   * Return a federated login button for staff users.
   */
  function getFederatedLoginButton() {
    if(renderedUserType & (userTypes.staff)) {
      return(
        <>
          <Button
            variant="secondary"
            size="large"
            onClick={() => Auth.federatedSignIn({
              provider: 'Cirencester-Friendly',
              customState: redirectInfo.getAsEncodedString()
            })}
          >
            Login with your corporate account
          </Button>
          
        </>
      );
    }
    return(null);
  }

  /**
   * Return a register button for FA-like users.
   */
  function getRegisterButton() {
    if(
      renderedUserType & (
        userTypes.ifa
        | userTypes.paraplanner
        | userTypes.administrator
      )
      &&
      config.cognito.allowRegister
      &&
      federatedInfo === null
    ) {
      return (
        <Button
          variant="secondary"
          size="large"
          as={NavLink}
          to="/register"
        >
          Register
        </Button>
      );
    }
    return(null);
  }

  /**
   * Return a forgot password button for non-staff users.
   */
   function getForgotPasswordButton() {
    if(
      renderedUserType & (
        userTypes.member
        | userTypes.ifa
        | userTypes.paraplanner
        | userTypes.administrator
      )
      &&
      federatedInfo === null
    ) {
      return (
        <Button
          variant="link"
          size="large"
          as={NavLink}
          to="/forgotpassword"
        >
          I forgot my password.
        </Button>
      );
    }
    return(null);
  }

  /**
   * Returns a button leading to the website FAQ for members.
   */
  function getIDontHaveLoginDetails() {
    if(
      renderedUserType & (
        userTypes.member
      )
    ) {
      if (!noLoginDetailsVisible) {
        return (
          <Button
            variant="secondary"
            size="large"
            //href="https://www.cirencester-friendly.co.uk/frequently-asked-questions"
            //onClick={(event) => outlink(event, "https://www.cirencester-friendly.co.uk/frequently-asked-questions")}
            onClick={() => setNoLoginDetailsVisible(!noLoginDetailsVisible)}
          >
            I don't have login details.
          </Button>
        )
      }
      else {
        return (
          <Alert>
            <h4>I don't have login details.</h4>
            <p>
              Your user account was created when your contract went on risk.
              Login details were sent to the email address provided to us by
              your financial adviser. If you didn't receive any details, but
              believe you should have, please contact your financial adviser.
            </p>
            <p>
              Alternatively, you can contact our member services team:<br/>
              Telephone: <a href="Tel: 0800 587 5098">0800 587 5098</a><br/>
              Email: <a href="mailto:memberservices@cirencester-friendly.co.uk">memberservices@cirencester-friendly.co.uk</a>
            </p>
          </Alert>
        );
      }
    }
    return(null);
  }

  /**
   * Returns a link directing the user to the user selection page.
   */
  function getUsertypeSelectionLink() {
    let linkText = 'I want to sign in as a different user type.';
    if(renderedUserType & (
      userTypes.ifa
      | userTypes.paraplanner
      | userTypes.administrator
    )) {
      linkText = 'I am not an adviser, paraplanner or firm administrator.';
    }
    else if(renderedUserType & (
      userTypes.staff
    )) {
      linkText = 'I am not CFS staff.';
    }
    else if(renderedUserType & (
      userTypes.member
    )) {
      linkText = 'I am not a member.';
    }
    // If the link is external we need to not use a react-router link.
    if (isAbsoluteURL(config.links.loginUserType)) {
      return(
        <Button  
          variant="link"
          size="large"
          href={`${config.links.loginUserType}${search}`}
        >
          {linkText}
        </Button>
      )
    }
    else {
      return (
        <Button
          variant="link"
          size="large"
          as={NavLink}
          to={`${config.links.loginUserType}${search}`}
        >
          {linkText}
        </Button>
      )
    }

  }

  /**
   * Returns captcha if it's enabled.
   */
  function getCaptcha() {
    if(
      config.captcha.enabled &&
      renderedUserType & (
        userTypes.member
        | userTypes.ifa
        | userTypes.paraplanner
        | userTypes.administrator
      )
      &&
      federatedInfo === null
    ) {
      
      return (
        <div className="recaptchaContainer">
          {
            captchaLoaded ? null : <p>Loading Captcha...</p>
          }
          <ReCAPTCHA
            sitekey = { config.captcha.siteKey }
            ref = { (ref) => { setCaptchaRef(ref) } }
            onChange = { 
              (value) => {
                setCaptchaValue(value)
              }
            }
            onErrored = { 
              (value) => {
                setCaptchaValue(null)
                setError(
                  new UiError('auth', "Something went wrong with the Captcha. Please make sure you have an internet connection and try refreshing the page.", 'danger')
                );
              }
            }
            onExpired = { () => setCaptchaValue(null) }
            asyncScriptOnLoad = { () => setCaptchaLoaded(true) }
          />
        </div>
      );
    }
    return(null);
  }

  /**
   * Returns the login form.
   */
  function getLoginForm() {
    return(
      <Form className="narrowform" onSubmit={handleSubmit}>
        <h1 className="h3 mb-2 font-weight-normal">
          {userTypeAsString(renderedUserType)} sign in.
        </h1>
        {getUsernamePassword()}
        <div className="d-grid gap-2 pt-2">
          {getCaptcha()}
          {getLoginButton()}
          {getFederatedLoginButton()}
          {getRegisterButton()}
          {getIDontHaveLoginDetails()}
          {getForgotPasswordButton()}
          {getUsertypeSelectionLink()}
        </div>
      </Form>
    );
  }

  function getUserSelectForm() {
    // In prod, the user may be redirected to the cfs website.
    // This happens in useEffect, as it may take a while we'll want to
    // prevent rendering the test page.
    const loginLink = config.links.loginUserType;
    if (
      !(
        process.env.REACT_APP_STAGE === 'production' &&
        isAbsoluteURL(loginLink)
      )
    ) {
      return(
        <>
          <Row className="pb-2">
            <Col>
              <h1>Welcome</h1>
              <p>Please choose your user type.</p>
            </Col>
          </Row>
          <Row>
            <Col>
              <div className="d-grid gap-2">
                <Button
                  className="mb-3"
                  variant="primary"
                  as={NavLink}
                  exact="true"
                  to={`/login/member${search}`}
                >
                  Member Login
                </Button>
              </div>
            </Col>
            <Col>
              <div className="d-grid gap-2">
                <Button
                  className="mb-3"
                  variant="secondary"
                  as={NavLink}
                  exact="true"
                  to={`/login/adviser${search}`}
                >
                  Adviser Login
                </Button>
              </div>
            </Col>
            <Col>
              <div className="d-grid gap-2">
                <Button
                  className="mb-3"
                  variant="dark-grey"
                  as={NavLink}
                  exact="true"
                  to={`/login/staff${search}`}
                >
                  Staff Login
                </Button>
              </div>
            </Col>
          </Row>
        </>
      );
    }
  }

  return (
    <Container fluid="sm">
      { renderedUserType === 0 ? getUserSelectForm() : getLoginForm() }
    </Container>
  );
}

Login.propTypes = {
  /**
   * Usertype as defined in userlib. Parsed from url. 
   */
  userType: PropTypes.string,
};

export default Login;